import React from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { StyledButton } from '@/common/components/button/StyledButton';
import { Recommendation } from '@/areas/culture-hub/types/api';
import { ActionPlanTaskKey } from '@/areas/culture-hub/slices/cultureHubInterventionsSlice';

interface MobileRecommendationCardProps {
  recommendation: Recommendation;
  selectedActionPlanTaskKeys: ActionPlanTaskKey[];
  onAdd: (recommendation: Recommendation) => void;
}

type StatusVariant = 'added' | 'recommended' | 'high' | 'medium' | 'low' | 'minimal';

const getRelevanceVariant = (score?: number, isAdded = false): StatusVariant => {
  if (isAdded) return 'added';
  if (score === undefined || score === null) return 'recommended';
  if (score >= 75) return 'high';
  if (score >= 50) return 'medium';
  if (score >= 25) return 'low';
  return 'minimal';
};

const getStatusText = (recommendation: Recommendation) => {
  if (recommendation.relevance_label) return recommendation.relevance_label;
  return 'Recommended';
};

const MobileRecommendationCard: React.FC<MobileRecommendationCardProps> = ({ recommendation, selectedActionPlanTaskKeys, onAdd }) => {
  const navigate = useNavigate();
  const isAdded = selectedActionPlanTaskKeys.some(
    (actionPlan) =>
      actionPlan.recommendation_template_id === recommendation.recommendation_template_id &&
      actionPlan.task_index === (recommendation.task_index ?? 0)
  );

  const badgeText = getStatusText(recommendation);
  const badgeVariant = getRelevanceVariant(recommendation.relevance_score, isAdded);

  const handleLearnMoreClick = () => {
    if (recommendation.recommendation_template_id == null) {
      return;
    }

    navigate(
      `/app/mobile/focus-areas/recommendation/${recommendation.recommendation_template_id}/${recommendation.task_index ?? 0}`,
      { state: { item: recommendation, source: 'pillars' } }
    );
  };

  return (
    <Card isAdded={isAdded}>
      <TopRightTag statusVariant={badgeVariant}>{badgeText}</TopRightTag>
      <CardHeader>
        <TitleBlock>
          <CardTitle>{recommendation.task || recommendation.task_title || recommendation.title || 'Recommendation'}</CardTitle>
          {recommendation.time ? <CardSubtitle>{recommendation.time}</CardSubtitle> : null}
        </TitleBlock>
      </CardHeader>
      {recommendation.description ? <Description>{recommendation.description}</Description> : null}
      <Footer>
        <ActionButtonGroup>
          <StyledButton
            label="Learn more"
            width="100%"
            height="36px"
            color="green"
            onClick={handleLearnMoreClick}
            disabled={recommendation.recommendation_template_id == null}
          />
          <StyledButton
            label={isAdded ? 'Added' : 'Add'}
            width="100%"
            height="36px"
            color="blue"
            onClick={() => onAdd(recommendation)}
            disabled={isAdded}
          />
        </ActionButtonGroup>
      </Footer>
    </Card>
  );
};

export default MobileRecommendationCard;

const Card = styled.button<{ isAdded: boolean }>`
  position: relative;
  background: ${(props) => (props.isAdded ? '#ecfdf5' : '#ffffff')};
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  padding: 20px 16px 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  text-align: left;
  cursor: pointer;
  width: 100%;
  box-sizing: border-box;
  min-width: 0;

  &:hover {
    background: ${(props) => (props.isAdded ? '#ecfdf5' : '#f8fafc')};
  }
`;

const CardHeader = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
`;

const TitleBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 100%;
  min-width: 0;
`;

const CardTitle = styled.h3`
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: #111827;
  min-width: 0;
  line-height: 1.2;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const CardSubtitle = styled.div`
  font-size: 13px;
  color: #475569;
  line-height: 1.4;
  overflow-wrap: anywhere;
`;

const StatusBadge = styled.span<{ statusVariant: StatusVariant }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 500;
  line-height: 1.2;
  border: 1px solid;
  flex-shrink: 0;
  max-width: 100%;
  white-space: nowrap;
  color: ${(props) => {
    switch (props.statusVariant) {
      case 'added':
        return '#047857';
      case 'high':
        return '#047857';
      case 'medium':
        return '#1d4ed8';
      case 'low':
        return '#b45309';
      default:
        return '#6b7280';
    }
  }};
  background: ${(props) => {
    switch (props.statusVariant) {
      case 'added':
        return '#dcfce7';
      case 'high':
        return '#ecfdf5';
      case 'medium':
        return '#eff6ff';
      case 'low':
        return '#fffbeb';
      default:
        return '#f3f4f6';
    }
  }};
  border-color: ${(props) => {
    switch (props.statusVariant) {
      case 'added':
        return '#a7f3d0';
      case 'high':
        return '#bbf7d0';
      case 'medium':
        return '#bfdbfe';
      case 'low':
        return '#fde68a';
      default:
        return '#e5e7eb';
    }
  }};
`;

const TopRightTag = styled(StatusBadge)`
  position: absolute;
  top: -11px;
  right: 16px;
  padding: 3px 10px;
`;

const Description = styled.p`
  margin: 0;
  color: #334155;
  font-size: 14px;
  line-height: 1.6;
  overflow-wrap: anywhere;
`;

const Footer = styled.div`
  display: flex;
  justify-content: stretch;
`;

const ActionButtonGroup = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  width: 100%;
`;

const MetaText = styled.span`
  color: #475569;
  font-size: 12px;
`;
